import { useCallback } from 'react';
import { type CombinedError } from 'urql';
import {
  type RewardNotApplied,
  type ValidateRewardFailureCode,
} from '@sg/graphql-schema';

import { useApplyRewardErrorMessages, useDaysOfTheWeekMessages } from './hooks';
import { type ApplyRewardErrorMessages } from './useApplyRewardErrorMessage.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const useApplyRewardErrorMessage = (params: Params) => {
  const { messages } = params;

  // ─── Messages ─────────────────────────────────────────────────────────

  const daysOfTheWeek = useDaysOfTheWeekMessages();

  const {
    defaultErrorMessage,
    networkErrorMessage,
    multipleErrorsMessage,
    getApplyRewardFailureCode,
    getApplyRewardFailureReason,
  } = useApplyRewardErrorMessages({ messages });

  // ─────────────────────────────────────────────────────────────────────

  return useCallback(
    (
      data: RewardNotApplied | { __typename?: string } | null | undefined,
      error: CombinedError | undefined,
    ) => {
      const isOtherError = data?.__typename !== 'RewardNotApplied';

      if (error || !data) {
        return networkErrorMessage;
      }

      if (isOtherError) {
        return defaultErrorMessage;
      }

      // ─────────────────────────────────────────────────────────────────────

      const errorData = data as RewardNotApplied;
      const { failureCode, failureReasons, failureMetadata } = errorData;

      const firstFailureReason = failureReasons[0];

      const hasMultipleFailureReasons = failureReasons.length > 1;
      const hasValidFailureReason = typeof firstFailureReason === 'string';
      const hasSingleFailureReason = failureReasons.length === 1 && hasValidFailureReason; // prettier-ignore

      const requiredChannel = failureMetadata.requiredChannel ?? '';
      const requiredDays: readonly number[] = failureMetadata.requiredDays ?? []; // prettier-ignore

      // ─────────────────────────────────────────────────────────────────────

      if (hasMultipleFailureReasons) {
        return multipleErrorsMessage;
      }

      if (hasSingleFailureReason) {
        const days = getDays(requiredDays, daysOfTheWeek);
        const failureReason = firstFailureReason as ValidateRewardFailureCode;
        const message = getApplyRewardFailureReason(failureReason);

        return message.replaceAll('(channel)', requiredChannel).replaceAll('(days)', days); // prettier-ignore
      }

      if (failureCode) {
        return getApplyRewardFailureCode(failureCode);
      }

      return defaultErrorMessage;
    },
    [
      defaultErrorMessage,
      networkErrorMessage,
      getApplyRewardFailureCode,
      multipleErrorsMessage,
      getApplyRewardFailureReason,
      daysOfTheWeek,
    ],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Given an array of Loyalty vendor days, return the localized representation for it.
 */
function getDays(
  requiredDays: readonly number[],
  daysOfTheWeek: ReturnType<typeof useDaysOfTheWeekMessages>,
): string {
  if (requiredDays.length === 0) {
    return '';
  }

  return requiredDays.map((day) => daysOfTheWeek[day]).filter(Boolean).join(', '); // prettier-ignore
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  messages: ApplyRewardErrorMessages | undefined;
};
